import React from 'react';
import { useTranslation } from 'react-i18next';

const ELLIPSIS = -1;
const WINDOW_SIZE = 5;

/**
 * Čísla stránek kolem aktuální; krajní stránky zůstávají vždy dostupné a mezera
 * se nahradí výpustkou. Bez toho se u stovek stránek nedá skočit jinam než o pár
 * pozic vedle — na `?page=213` z 300 se člověk musí proklikat.
 */
export function buildPageNumbers(page: number, totalPages: number): number[] {
  if (totalPages <= WINDOW_SIZE + 2) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const half = Math.floor(WINDOW_SIZE / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + WINDOW_SIZE - 1);
  // U konce seznamu okno doplníme dopředu, ať má pořád WINDOW_SIZE položek
  start = Math.max(1, end - WINDOW_SIZE + 1);

  const pages: number[] = [];
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push(ELLIPSIS);
  }
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(ELLIPSIS);
    pages.push(totalPages);
  }
  return pages;
}

interface TablePaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  showingLabel?: string;
}

const TablePagination: React.FC<TablePaginationProps> = ({ page, totalPages, total, pageSize, onPageChange, showingLabel }) => {
  const { t } = useTranslation('admin');

  if (totalPages <= 1) return null;

  const hasPrev = page > 1;
  const hasNext = page < totalPages;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={!hasPrev}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common.previous')}
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={!hasNext}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common.next')}
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            {showingLabel ?? t('common.showingResults', { from, to, total })}
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(1)}
              disabled={!hasPrev}
              aria-label={t('common.firstPage')}
              title={t('common.firstPage')}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={!hasPrev}
              aria-label={t('common.previous')}
              title={t('common.previous')}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {pageNumbers.map((pageNum, idx) =>
              pageNum === ELLIPSIS ? (
                <span
                  key={`gap-${idx}`}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm text-gray-400 select-none"
                >
                  …
                </span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm ${
                    page === pageNum
                      ? 'z-10 !bg-surface-dark-primary !border-surface-dark-primary !text-white font-bold'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 font-medium'
                  }`}
                >
                  {pageNum}
                </button>
              ),
            )}

            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={!hasNext}
              aria-label={t('common.next')}
              title={t('common.next')}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={!hasNext}
              aria-label={t('common.lastPage')}
              title={t('common.lastPage')}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
